import { GenericLazyString } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import {
    DATE_FILTER_DISPLAY_VALUES,
    DATE_FILTER_VALUES,
} from '../../constants/dates';
import { DateFilterButton } from './DateFilterButton';
import './DateFilterDropdown.scss';

const LazyDropdown = loadable.lib(() => import('@eventbrite/eds-containers'));
const LazyDatePicker = loadable.lib(
    () => import('@eventbrite/eds-date-picker'),
);
const LazyTextList = loadable.lib(() => import('@eventbrite/eds-text-list'));

interface DateFilterRendererProps {
    resetDateParams: () => void;
    onCustomDateSelect: (
        {
            startDate,
            endDate,
        }: {
            startDate: string;
            endDate: string;
        },
        callback: () => void,
    ) => void;
    onTextItemSelect: (value: string, callback: () => void) => void;
    startDate?: string;
    date?: string;
    endDate?: string;
    dropdownItems: { content: string | GenericLazyString; value: string }[];
    locale?: string;
}

const minimalFormat = (dateString?: string, locale?: string) => {
    if (!dateString) return;

    const modifiedLocale = locale?.replace('_', '-') || 'en-US';
    const modifiedDateString = `${dateString}T00:00:00`;
    const date = new Date(modifiedDateString);

    const formattedDate = new Intl.DateTimeFormat(modifiedLocale, {
        month: 'short',
        day: 'numeric',
    }).format(date);

    return formattedDate;
};

export const DateFilterDropdown: React.FunctionComponent<
    DateFilterRendererProps
> = (props) => {
    const [renderDatePicker, setRenderDatePicker] = React.useState<
        boolean | null
    >(null);
    const [renderDropdown, setRenderDropdown] = React.useState<boolean | null>(
        null,
    );

    const resetDisplay = () => {
        setRenderDatePicker(false);
        setRenderDropdown(false);
    };

    const hasDate = !!props.date || !!props.startDate;

    return (
        <div className="discover__date-filter">
            <DateFilterButton
                onClose={props.resetDateParams}
                onClick={() => setRenderDropdown(true)}
                hasActiveDate={hasDate}
            >
                {DATE_FILTER_DISPLAY_VALUES[props?.date || ''] ||
                    `${minimalFormat(
                        props.startDate,
                        props.locale,
                    )} - ${minimalFormat(props.endDate, props.locale)}`}
            </DateFilterButton>
            {!!renderDropdown ? (
                <LazyDropdown>
                    {({ Dropdown, ALIGN_RIGHT }: any) => (
                        <Dropdown
                            onClickOutside={resetDisplay}
                            align={ALIGN_RIGHT}
                        >
                            {!!renderDatePicker ? (
                                <LazyDatePicker>
                                    {({
                                        InlineDateRangePicker,
                                        BLUE_COLOR,
                                    }: any) => (
                                        <InlineDateRangePicker
                                            minDate={new Date().toISOString()}
                                            defaultValue={{
                                                startDate: props.startDate,
                                                endDate: props.endDate,
                                            }}
                                            color={BLUE_COLOR}
                                            onChange={(val: {
                                                startDate: string;
                                                endDate: string;
                                            }) =>
                                                props.onCustomDateSelect(
                                                    val,
                                                    resetDisplay,
                                                )
                                            }
                                            initialFocusedInput="startDate"
                                        />
                                    )}
                                </LazyDatePicker>
                            ) : (
                                <LazyTextList>
                                    {({ TextList }: any) => (
                                        <TextList
                                            id="discover__date-filter--items"
                                            items={props.dropdownItems}
                                            onItemSelect={(val: string) => {
                                                if (
                                                    val ===
                                                    DATE_FILTER_VALUES.CUSTOM_DATE
                                                )
                                                    return setRenderDatePicker(
                                                        true,
                                                    );
                                                props.onTextItemSelect(
                                                    val,
                                                    resetDisplay,
                                                );
                                            }}
                                        />
                                    )}
                                </LazyTextList>
                            )}
                        </Dropdown>
                    )}
                </LazyDropdown>
            ) : null}
        </div>
    );
};
